import { Step } from "../../../types/Step";
import { DiabeticFormEvaluator } from "../../Steps/Medical/Compositions/ComposedDiabeticMedicalQuestions";
import { GeneralMedicalQuestionsEvaluator } from "../../Steps/Medical/Compositions/ComposedGeneralMedicalQuestions";
import { PacemakerFormEvaluator } from "../../Steps/Medical/Compositions/ComposedPacemakerMedicalQuestions";
import { RadiationFormEvaluator } from "../../Steps/Medical/Compositions/ComposedRadiationMedicalQuestions";
import { FlowStepsConfiguration } from "../../Types/IFlow";
import NavigatorItemId from "../Types/NavigatorItemId";

/** The configured steps for screening qualified customers including qualify and medical steps. */
export const screeningStepConfigurations: FlowStepsConfiguration = {
  [Step.qualify]: {
    activeNavigatorItem: NavigatorItemId.Screening,
    key: Step.qualify,
    nextStep: ({ customerCState: { height, weight, dateOfBirth }, disqualifyingReasonsCState }) =>
      height && weight && dateOfBirth && disqualifyingReasonsCState ? Step.medicalGeneral : undefined
  },
  [Step.medicalGeneral]: {
    activeNavigatorItem: NavigatorItemId.Screening,
    key: Step.medicalGeneral,
    nextStep: ({ medicalForm, disqualifyingReasonsCState }) => {
      if (disqualifyingReasonsCState && GeneralMedicalQuestionsEvaluator.isComplete(medicalForm))
        return Step.medicalPacemaker;
      return undefined;
    }
  },
  [Step.medicalPacemaker]: {
    activeNavigatorItem: NavigatorItemId.Screening,
    key: Step.medicalPacemaker,
    nextStep: ({ medicalForm, disqualifyingReasonsCState }) => {
      if (disqualifyingReasonsCState && PacemakerFormEvaluator.isComplete(medicalForm)) return Step.medicalRadiation;
      return undefined;
    }
  },
  [Step.medicalRadiation]: {
    activeNavigatorItem: NavigatorItemId.Screening,
    key: Step.medicalRadiation,
    nextStep: ({ medicalForm, disqualifyingReasonsCState }) => {
      if (disqualifyingReasonsCState && RadiationFormEvaluator.isComplete(medicalForm)) return Step.medicalDiabetic;
      return undefined;
    }
  },
  [Step.medicalDiabetic]: {
    activeNavigatorItem: NavigatorItemId.Screening,
    key: Step.medicalDiabetic,
    nextStep: ({ medicalForm, disqualifyingReasonsCState }) => {
      if (disqualifyingReasonsCState && DiabeticFormEvaluator.isComplete(medicalForm)) return Step.scheduling;
      return undefined;
    }
  }
};

/** The configured scheduling and booking steps that support changing the center. */
export const centerSelectableBookingStepConfigurations: FlowStepsConfiguration = {
  [Step.scheduling]: {
    activeNavigatorItem: NavigatorItemId.Scheduling,
    key: Step.scheduling,
    nextStep: ({ reservation }) => (reservation && Step.confirmation) || undefined
  },
  [Step.centerSelection]: {
    activeNavigatorItem: NavigatorItemId.Scheduling,
    key: Step.centerSelection,
    nextStep: () => Step.scheduling
  },
  [Step.confirmation]: {
    activeNavigatorItem: NavigatorItemId.Confirmation,
    key: Step.confirmation,
    nextStep: null
  }
};

/** The configured scheduling and booking steps that do not allow for the center to be changed. */
export const staticCenterBookingStepConfigurations: FlowStepsConfiguration = {
  [Step.scheduling]: centerSelectableBookingStepConfigurations[Step.scheduling],
  [Step.confirmation]: centerSelectableBookingStepConfigurations[Step.confirmation]
};

/** The configured scheduling steps that support changing the center, with no confirmation step (books directly). */
export const noConfirmationCenterSelectableBookingStepConfigurations: FlowStepsConfiguration = {
  [Step.scheduling]: {
    activeNavigatorItem: NavigatorItemId.Scheduling,
    key: Step.scheduling,
    nextStep: null
  },
  [Step.centerSelection]: {
    activeNavigatorItem: NavigatorItemId.Scheduling,
    key: Step.centerSelection,
    nextStep: () => Step.scheduling
  }
};

/** The configured scheduling steps that do not allow for the center to be changed, with no confirmation step (books directly). */
export const noConfirmationStaticCenterBookingStepConfigurations: FlowStepsConfiguration = {
  [Step.scheduling]: noConfirmationCenterSelectableBookingStepConfigurations[Step.scheduling]
};
